/* Is the API answering? (owner, 2026-11-01: "We'll be back shortly" while a
   release holds the API.)

   Nothing is checked while things work: no timer, no check on page load.
   A check starts ONLY after an API request fails (no answer, or 502/503/504):
   - it asks /healthz (no database, no Redis); if that answers, it stops;
   - if not, it asks again after 10 s, 20 s, 40 s, 80 s, then every 2 minutes,
     until /healthz answers, and then stops;
   - the banner shows only after TWO failed checks in a row (one dropped
     request on a phone is not an outage), and goes away on the first answer.
   Every component reads the same answer, so one tab asks at most once per wait. */
import { useEffect, useState } from "react";
import { API_BASE } from "../api/api";

export const RETRY_WAITS_MS = [10000, 20000, 40000, 80000, 120000];
const TIMEOUT_MS = 8000;

let down = false;
let failures = 0;
let timer = null;
let checking = false;
const listeners = new Set();

function setDown(next) {
  if (next === down) return;
  down = next;
  listeners.forEach((fn) => fn(down));
}

async function healthy() {
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const t = ctrl ? setTimeout(() => ctrl.abort(), TIMEOUT_MS) : null;
  try {
    const r = await _realFetch()(`${API_BASE}/healthz`, { cache: "no-store", signal: ctrl?.signal });
    return r.ok;
  } catch {
    return false;
  } finally {
    if (t) clearTimeout(t);
  }
}

async function runCheck() {
  timer = null;
  const ok = await healthy();
  if (ok) {
    failures = 0;
    checking = false;
    setDown(false);
    return;
  }
  failures += 1;
  if (failures >= 2) setDown(true);
  const wait = RETRY_WAITS_MS[Math.min(failures - 1, RETRY_WAITS_MS.length - 1)];
  timer = setTimeout(runCheck, wait);
}

/** An API request just failed: start checking, unless already checking. */
export function reportPossibleOutage() {
  if (checking) return;
  checking = true;
  runCheck();
}

// The fetch the check itself uses, kept from before the watcher wraps it,
// so the check's own failures never re-trigger a check.
let originalFetch = null;
function _realFetch() {
  return originalFetch || window.fetch;
}

/** Called once at start-up (src/index.js): watches every request to the API
    and reports a failure. It only observes: answers and errors pass through
    unchanged. */
export function installApiFailureWatch() {
  if (originalFetch || typeof window.fetch !== "function") return;
  originalFetch = window.fetch;
  const real = originalFetch;
  window.fetch = function watchedFetch(input, init) {
    const url = typeof input === "string" ? input : input?.url || "";
    const isApi = url.startsWith(API_BASE);
    const p = real.call(this, input, init);
    if (!isApi) return p;
    return p.then(
      (r) => {
        if ([502, 503, 504].includes(r.status)) reportPossibleOutage();
        return r;
      },
      (err) => {
        if (err?.name !== "AbortError") reportPossibleOutage();
        throw err;
      },
    );
  };
}

/** True while the API is down (two failed checks). Never starts a check. */
export function useApiDown() {
  const [value, setValue] = useState(down);
  useEffect(() => {
    listeners.add(setValue);
    setValue(down);
    return () => listeners.delete(setValue);
  }, []);
  return value;
}

/** For tests only. */
export function _resetApiStatus() {
  clearTimeout(timer);
  if (originalFetch) window.fetch = originalFetch;
  down = false; failures = 0; timer = null; checking = false; originalFetch = null;
  listeners.clear();
}
